// WidgetFrame.js
// The chrome every dashboard card shares: a slim uppercase title row, the
// hover-revealed actions, a scrolling body, and the provenance line that says
// which run painted this card.
//
// The frame is deliberately ignorant of what it wraps — it takes a resolved
// title and a run document, nothing else — so a stat tile, a chart, a file and
// the whole tabbed composite all sit in identical chrome and a view reads as one
// surface rather than a pile of unrelated panels.

import React, { useMemo } from 'react';
import useNow from '../../../hooks/useNow';
import RunHistorySaveWarning from '../../common/RunHistorySaveWarning';
import CardVersionNotice from './CardVersionNotice';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { RunOriginIcon } from '../../common/RunChrome';
import { describeRunParameters, formatRunTime, runOriginLabel } from '../../../utils/runDisplay';

function WidgetFrame({
  title,
  run = null,
  // The run's document is on its way (a re-run whose output arrived first), or
  // its subscription failed: the output is shown, the provenance line says which.
  runPending = false,
  runDocError = false,
  historySaveUnconfirmed = false,
  onRetryRunDoc = null,
  // What the card has to say about the version it runs (see CardVersionNotice):
  // shown above the body, never instead of it.
  versionNotice = null,
  onDismissVersionNotice = null,
  // The function's draft has edits its published version does not: this card,
  // its runs, Automations and emails keep the published version until it is
  // published again, and the provenance line says so.
  hasUnpublishedChanges = false,
  loading = false,
  // A new run is in flight for a card that already shows content: the body is
  // greyed out under a spinner (the same busy overlay as the Data page table)
  // rather than blanked, so the previous result stays readable underneath.
  busy = false,
  onConfigure = null,
  extraActions = null,
  draggable = false,
  showProvenance = true,
  bodyPadding = undefined,
  children,
}) {
  // The provenance line is relative ("14m ago", "Yest"), so it follows the
  // shared clock.
  useNow();

  // The provenance line only says WHEN and HOW the run happened; the parameters
  // it executed with are the other half of "why does this card show this
  // number", so they hang off the same line as a tooltip.
  const parameterLines = useMemo(() => (run ? describeRunParameters(run) : []), [run]);

  const provenanceTooltip = parameterLines.length > 0
    ? (
      <Box>
        {parameterLines.map((line, index) => (
          <Typography key={`${line}-${index}`} variant="caption" sx={{ display: 'block' }}>
            {line}
          </Typography>
        ))}
      </Box>
    )
    : 'No parameters recorded for this run.';

  return (
    <Paper
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 1.5,
        p: 1.25,
        '&:hover .widget-hover-actions': { opacity: 1 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, mb: 0.75 }}>
        {/* The grip is the only way to move a widget, so it never hides behind
            hover — it is only subdued. */}
        {draggable && (
          <Box className="widget-drag-handle" sx={{ cursor: 'grab', display: 'flex', color: 'text.disabled' }}>
            <DragIndicatorIcon fontSize="small" />
          </Box>
        )}
        <Typography
          variant="caption"
          noWrap
          sx={{
            flex: 1,
            minWidth: 0,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'text.secondary',
          }}
        >
          {title}
        </Typography>
        {loading && <CircularProgress size={14} />}
        <Box
          className="widget-hover-actions"
          sx={{ opacity: 0, transition: 'opacity .15s', display: 'flex', gap: 0.25 }}
        >
          {extraActions}
          {onConfigure && (
            <Tooltip title="Card display">
              <IconButton size="small" onClick={onConfigure}>
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            ...(bodyPadding === undefined ? null : { p: bodyPadding }),
          }}
        >
          {historySaveUnconfirmed && <RunHistorySaveWarning />}
          {versionNotice && (
            <CardVersionNotice notice={versionNotice} onDismiss={onDismissVersionNotice} />
          )}
          {children}
        </Box>
        {busy && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              zIndex: 2,
              backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}
      </Box>

      {showProvenance && !run && (runPending || runDocError) && (
        <Box sx={{ marginTop: 'auto', paddingTop: 1, display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.disabled' }}>
          {runPending && <CircularProgress size={10} color="inherit" />}
          <Typography variant="caption" color={runDocError ? 'warning.main' : 'text.disabled'}>
            {runDocError ? 'Run details unavailable' : 'Updating run details…'}
          </Typography>
          {runDocError && onRetryRunDoc && (
            <Button size="small" onClick={onRetryRunDoc} sx={{ textTransform: 'none', minWidth: 0, py: 0, px: 0.5, fontSize: '0.75rem' }}>
              Retry
            </Button>
          )}
        </Box>
      )}
      {showProvenance && run && (
        <Tooltip title={provenanceTooltip}>
          <Box
            sx={{
              marginTop: 'auto',
              paddingTop: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              flexWrap: 'wrap',
              color: 'text.disabled',
            }}
          >
            <RunOriginIcon triggerType={run.trigger_type} fontSize={12} />
            <Typography variant="caption" color="text.disabled">
              {`${runOriginLabel(run.trigger_type)} · ${formatRunTime(run.created_at)}`}
            </Typography>
            {/* A run written before function versions existed has none; the pill
                is dropped rather than rendered as a bare "v". */}
            {(run.function_version || run.function_version === 0) && (
              <Box
                component="span"
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 5,
                  px: 0.75,
                  fontSize: 10,
                  lineHeight: 1.6,
                }}
              >
                {`v${run.function_version}`}
              </Box>
            )}
            {hasUnpublishedChanges && (
              <Typography variant="caption" color="warning.main" sx={{ fontSize: 10 }}>
                unpublished changes
              </Typography>
            )}
          </Box>
        </Tooltip>
      )}
    </Paper>
  );
}

export default WidgetFrame;
